import { Component } from '@theme/component';

/**
 * Announcement banner with smooth horizontal slides.
 * Stable height, true centering, swipe support, reduced-motion safe.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  /** @type {number|undefined} */ #interval = undefined;

  /** @type {HTMLElement|null} */ #track = null;
  /** @type {number} */ #slideCount = 0;

  // swipe state
  #pointerActive = false;
  #startX = 0;
  #deltaX = 0;
  #dragBaseIndex = 0;

  // stable height lock
  #lockedHeight = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#mountTrackOnce();

    // Hide theme injected arrow controls (if any)
    this.querySelectorAll('.slideshow-control, .slider-button, .slideshow__controls, .icon.icon-caret')
      .forEach(el => (el.style.display = 'none')); // keep DOM clean and avoid reflow jitter [web:7]

    // Initial layout and aria
    this.#slideCount = (this.refs.slides ?? []).length;
    if (this.#slideCount === 0) return;

    // 1) First paint: no transition, snap to index 0
    this.#applyTransform(0, false);
    this.#updateAria(0);

    // 2) Force reflow to lock the transform without animating
    // eslint-disable-next-line no-unused-expressions
    this.#track && this.#track.offsetWidth;

    // 3) On next frame: compute and lock consistent height, enable transitions, start autoplay
    requestAnimationFrame(() => {
      this.#lockConsistentHeight(); // prevents jumpy bar height [web:7][web:10][web:16]
      this.#enableAnimatedTransitions();
      this.play();
    });

    // Event listeners (after mount)
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Swipe events
    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    this.addEventListener('pointercancel', this.#onPointerUp, { passive: true });
    this.addEventListener('pointerleave', this.#onPointerUp, { passive: true });

    // Resize observer to keep the bar stable if fonts/images load later
    this.#observeResize();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.#unobserveResize();
  }

  // Public navigation
  next() { this.current = this.#normalizedIndex(this.current + 1); }
  previous() { this.current = this.#normalizedIndex(this.current - 1); }

  // Autoplay control
  get autoplayInterval() {
    const raw = this.getAttribute('autoplay');
    const seconds = parseInt(`${raw}`, 10);
    if (Number.isNaN(seconds)) return undefined;
    return seconds * 1000;
  }
  get autoplay() { return Boolean(this.autoplayInterval); }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.paused = false;
    this.suspend();
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }
  pause() { this.paused = true; this.suspend(); }
  get paused() { return this.hasAttribute('paused'); }
  set paused(v) { this.toggleAttribute('paused', v); }
  suspend() { clearInterval(this.#interval); this.#interval = undefined; }
  resume() { if (!this.autoplay || this.paused) return; this.pause(); this.play(); }

  // Current index with safe normalization
  get current() { return this.#current; }
  set current(idx) {
    if (!this.#slideCount) return;
    const normalized = this.#normalizedIndex(idx);
    this.#current = normalized;
    this.#applyTransform(normalized, true);
    this.#updateAria(normalized);
  }

  // Normalize index into [0, slideCount)
  #normalizedIndex(i) {
    const n = this.#slideCount;
    if (!n) return 0;
    let r = i % n;
    if (r < 0) r += n;
    return Math.round(r);
  }

  // Build track one time and move slides into it preserving order
  #mountTrackOnce() {
    const container = this.refs.slideshowContainer ?? this;

    if (container.firstElementChild?.classList?.contains('announcement-track')) {
      this.#track = container.firstElementChild;
      if (!this.refs.slides || this.refs.slides.length === 0) {
        this.refs.slides = Array.from(this.#track.querySelectorAll('slideshow-slide'));
      }
    } else {
      const slides = Array.from(this.querySelectorAll('slideshow-slide'));
      const track = document.createElement('div');
      track.className = 'announcement-track';

      // Normalize each slide for true centering and stable dimensions [web:1][web:2][web:3]
      slides.forEach(slide => {
        slide.classList.add('announcement-bar__slide');
        Object.assign(slide.style, {
          // Flex centering for both axes
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Prevent nested content from breaking height
          textAlign: 'center',
          whiteSpace: 'normal',
          // As a flex-item in track
          flex: '0 0 100%',
          minWidth: '100%',
          maxWidth: '100%',
          // GPU hint and safety
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          // Contain layout/paint for stability
          contain: 'content'
        });
        // Make inner text wrappers behave and not introduce extra margins that change height [web:1]
        const blockish = slide.querySelectorAll('p,h1,h2,h3,h4,h5,h6');
        blockish.forEach(el => {
          el.style.marginTop = '0';
          el.style.marginBottom = '0';
        });

        track.appendChild(slide);
      });

      container.appendChild(track);
      (container instanceof HTMLElement) && Object.assign(container.style, {
        overflow: 'hidden',
        // Prevent line-height or font-loading jitter from collapsing container
        display: 'block'
      });

      this.#track = track;
      this.refs.slides = slides;
    }

    // Track base styles with transitions disabled initially (enabled later)
    Object.assign(this.#track.style, {
      display: 'flex',
      width: '100%',
      // Prevent height jumps by letting slides size themselves but track will be locked later
      alignItems: 'stretch',
      willChange: 'transform',
      transitionProperty: 'transform',
      transitionDuration: '0ms',
      transitionTimingFunction: 'cubic-bezier(.2,.8,.2,1)'
    });
  }

  #enableAnimatedTransitions() {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.#track.style.transitionDuration = prefersReduced ? '1ms' : '420ms'; // reduced‑motion friendly [web:12][web:9][web:18][web:20]
  }

  // Translate to slide index
  #applyTransform(index, animated) {
    if (!this.#track) return;

    // Toggle transition per call to avoid animating initial snap
    if (animated) this.#enableAnimatedTransitions();
    else this.#track.style.transitionDuration = '0ms';

    const offset = index * 100; // integral percent
    this.#track.style.transform = `translate3d(-${offset}%, 0, 0)`;
  }

  // Update aria state per slide
  #updateAria(active) {
    (this.refs.slides ?? []).forEach((slide, i) => {
      const isActive = i === active;
      slide.setAttribute('aria-hidden', String(!isActive));
      if (isActive) slide.setAttribute('aria-live', 'polite');
      else slide.removeAttribute('aria-live');
    });
  }

  // Compute tallest slide and lock track/container height to prevent jumps [web:7][web:10][web:16]
  #lockConsistentHeight() {
    if (!this.#track) return;
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    // Measure natural heights (allow wrap)
    let maxH = 0;
    slides.forEach(s => {
      // Temporarily ensure natural height is measurable
      const prevMinH = s.style.minHeight;
      s.style.minHeight = 'auto';
      const h = s.getBoundingClientRect().height;
      if (h > maxH) maxH = h;
      s.style.minHeight = prevMinH;
    });

    // Fallback to a minimum of one line-height if somehow zero
    if (maxH <= 0) maxH = 1;

    this.#lockedHeight = Math.ceil(maxH);

    // Lock both track and container to prevent reflow jumps while sliding
    this.#track.style.height = `${this.#lockedHeight}px`;
    const container = this.refs.slideshowContainer ?? this;
    if (container instanceof HTMLElement) {
      container.style.height = `${this.#lockedHeight}px`;
    }

    // Ensure each slide at least fills the locked height for vertical centering [web:1][web:2]
    (this.refs.slides ?? []).forEach(s => {
      s.style.minHeight = `${this.#lockedHeight}px`;
    });
  }

  // Observe later changes (web fonts, images, responsive) to recompute once stabilized [web:7][web:16]
  #resizeObserver = null;
  #observeResize() {
    if (!('ResizeObserver' in window)) return;
    const container = this.refs.slideshowContainer ?? this;
    if (!container) return;

    let rafId = 0;
    this.#resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Re-compute lock only if content grew larger than current lock
        const prev = this.#lockedHeight;
        this.#lockConsistentHeight();
        // If lock shrank, keep the larger of previous/new to avoid flicker between breakpoints
        if (prev > 0 && this.#lockedHeight < prev) {
          this.#lockedHeight = prev;
          this.#track.style.height = `${prev}px`;
          if (container instanceof HTMLElement) container.style.height = `${prev}px`;
          (this.refs.slides ?? []).forEach(s => (s.style.minHeight = `${prev}px`));
        }
      });
    });

    this.#resizeObserver.observe(container);
  }
  #unobserveResize() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  // Swipe handling
  #onPointerDown = (e) => {
    // only primary button or touch [web:13]
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    if (!this.#slideCount) return;
    this.#pointerActive = true;
    this.#startX = e.clientX;
    this.#deltaX = 0;
    this.#dragBaseIndex = this.current;

    // Disable transition during drag for direct tracking
    if (this.#track) this.#track.style.transitionDuration = '0ms';
  };

  #onPointerMove = (e) => {
    if (!this.#pointerActive || !this.#track) return;
    this.#deltaX = e.clientX - this.#startX;

    const width = this.clientWidth || 1;
    const progress = (this.#deltaX / width) * 100;

    // Limit visual drag to one slide each side
    const limited = Math.max(-100, Math.min(100, progress));
    const base = this.#normalizedIndex(this.#dragBaseIndex) * 100;
    this.#track.style.transform = `translate3d(calc(-${base}% + ${limited}%), 0, 0)`;
  };

  #onPointerUp = () => {
    if (!this.#pointerActive) return;
    this.#pointerActive = false;

    this.#enableAnimatedTransitions();

    const width = this.clientWidth || 1;
    const threshold = Math.max(30, width * 0.08); // 8% or 30px
    if (this.#deltaX > threshold) this.previous();
    else if (this.#deltaX < -threshold) this.next();
    else this.current = this.#dragBaseIndex; // snap back

    this.#deltaX = 0;
  };

  // Pause when hidden, resume when visible
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
